/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect } from "react";
import { refreshAuth } from "../../app/auth/authThunk";
import { logout, setPageAccess } from "../../app/auth/authSlice";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useNavigate, useLocation } from "react-router-dom";
import useVerifyToken from "./useVerifyToken";
import setAuthToken from "../../utils/setAuthToken";
import { chatWSDisconnecting } from "../../app/chat/chatSlice";

import { setAvailable } from "../../app/userState/userStateSlice";
import Layout from "../../layouts/layout-authorized";
import { dashboardWSDisconnecting } from "../../app/dashboard/dashboardSlice";
import DMSPlugin from "../dms-plugin";
import VEPlugin from "../ve-plugin";
import { sendLogoutStatus } from "../../app/auth/api";

interface IProps {
  children: JSX.Element;
  showSidebar?: boolean;
}

interface IToken {
  token: string,
  refreshToken: string,
  channelToken: string
}

const unauthorizedRoutes = [
  "/",
  "/dashboard",
  "/chat",
  "/chat/:chatId",
  "/profile",
  "/profile/security",
  "/profile/change-password",
  "/profile/edit",
];

const adminRoles = ["TOWR", "TMGR", "TADM"];

const PrivateRoute: FC<IProps> = ({ children, showSidebar = true }) => {
  const { isAuthenticated, routes, user } = useAppSelector(
    (state) => state.auth
  );
  const dispatch = useAppDispatch();
  const navigateTo = useNavigate();
  const location = useLocation();
  const accessToken = localStorage.getItem("access-token") ?? "";
  const refreshToken = localStorage.getItem("refresh-token") ?? "";
  const channelToken = localStorage.getItem("channel-access-token") ?? "";

  useEffect(() => {
    if (!isAuthenticated && accessToken) {
      setAuthToken({
        token: accessToken,
        refreshToken,
        channelToken
      });

      dispatch(refreshAuth(accessToken)).catch((e) => console.log(e));
    }
  }, []);

  const { response, error } = useVerifyToken(accessToken ?? "", location, isAuthenticated);
  const logoutProcess = async () => {
    dispatch(chatWSDisconnecting());
    dispatch(dashboardWSDisconnecting());
    dispatch(setAvailable(false));
    dispatch(logout());
    await sendLogoutStatus(user);
  }
  useEffect(() => {
    if (response) {
      const { status } = response;
      if (status === 0 && user?.username) {
        logoutProcess().catch(e => console.log("error logout private route", e))
      }
    }
  }, [response, user]);

  useEffect(() => {
    if (error) {
      dispatch(chatWSDisconnecting());
      dispatch(dashboardWSDisconnecting());
      dispatch(setAvailable(false));
      dispatch(logout());
      navigateTo(`signin`);
    }
    else if (response) {

      const { data } = response;
      const newtoken = (data as IToken)
      setAuthToken({ token: newtoken.token, refreshToken: newtoken.refreshToken, channelToken: newtoken.channelToken });
      dispatch(refreshAuth(newtoken.token)).catch((e) => console.log(e));

    }
  }, [response, error]);

  useEffect(() => {
    if (!accessToken) {
      navigateTo("/signin", { replace: true });
    }
  }, [accessToken]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const currentPath = location.pathname.split("?")[0];

    const isWhiteListed =
      unauthorizedRoutes.findIndex((r) => r === currentPath) > -1 ||
      unauthorizedRoutes
        .filter((r) => r.lastIndexOf("/") === currentPath.lastIndexOf("/"))
        .filter((r) => r.indexOf(":") > -1).length > 0;

    const isAdmin = adminRoles.findIndex((r) => r === user?.roleName) > -1;

    if (isWhiteListed || isAdmin) return;

    const isAuthorized =
      routes.filter((r) => r.route.indexOf(currentPath) > -1).length > 0 ||
      routes
        .filter(
          (r) => r.route.lastIndexOf("/") === currentPath.lastIndexOf("/")
        )
        .filter((r) => r.route.indexOf(":") > -1).length > 0;

    if (!isAuthorized) navigateTo("/error-403", { replace: true });
  }, [location]);

  useEffect(() => {
    const currentRoute = location.pathname.split("?")[0];
    const currentRouteSegments = currentRoute.split("/");

    const sameLengthRoutes = routes.filter(
      (r) => r.route.split("/").length === currentRouteSegments.length
    );

    const checkIsValid = ({ isDynamicSegment, routeSegments, i, segment }: any) => {
      let isValid = true;

      if (isDynamicSegment) {
        if (!routeSegments[i].match(/([A-Za-z0-9-])\w+/g)) {
          isValid = false;
        }
      } else if (segment !== currentRouteSegments[i]) {
        isValid = false;
      }

      return isValid;
    }

    for (const route of sameLengthRoutes) {
      const routeSegments = route.route.split("/");

      let isValid = true;

      for (let i = 0; i < routeSegments.length; i++) {
        const segment = routeSegments[i];
        const isDynamicSegment = segment.indexOf(":") > -1;

        isValid = checkIsValid({ isDynamicSegment, routeSegments, i, segment });
      }

      if (isValid) {
        dispatch(setPageAccess(route));
        break;
      }
    }
  }, [location, user]);

  return (
    <>
      <DMSPlugin></DMSPlugin>
      <VEPlugin></VEPlugin>
      <Layout showSidebar={showSidebar}>{children}</Layout>
    </>
  );
};

export default PrivateRoute;
